import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Eye, EyeOff } from 'lucide-react'

import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

type AuthPasswordFieldProps = Omit<React.ComponentProps<typeof Input>, 'type'> & {
  label: React.ReactNode
  errorMessage?: React.ReactNode
  action?: React.ReactNode
}

export const AuthPasswordField = React.forwardRef<HTMLInputElement, AuthPasswordFieldProps>(
  ({ label, errorMessage, action, className, disabled, id, ...props }, ref) => {
    const { t } = useTranslation()
    const [revealed, setRevealed] = React.useState(false)

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-4">
          <label className="text-sm font-medium text-foreground" htmlFor={id}>
            {label}
          </label>
          {action}
        </div>

        <div className="relative">
          <Input
            ref={ref}
            id={id}
            type={revealed ? 'text' : 'password'}
            disabled={disabled}
            className={cn('pr-12', className)}
            {...props}
          />
          <button
            type="button"
            onClick={() => setRevealed((value) => !value)}
            disabled={disabled}
            aria-label={revealed ? t('auth.hidePassword') : t('auth.showPassword')}
            className="absolute inset-y-0 right-0 inline-flex w-11 items-center justify-center text-muted-foreground transition hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
          >
            {revealed ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          </button>
        </div>

        {errorMessage ? (
          <p className="text-sm text-rose-700">{errorMessage}</p>
        ) : null}
      </div>
    )
  },
)

AuthPasswordField.displayName = 'AuthPasswordField'
